import { Link } from 'react-router-dom'

import {
    Chip,
    PrincipleChip,
    ScopeChip,
    strongestVerification,
    VerificationBadge,
} from '@/components/common'
import { useI18n } from '@/i18n'
import type { Fip, Organization, ResourceDetail } from '@/types'

export function ResourceCard({ resource }: { resource: ResourceDetail }) {
    const { t, localized } = useI18n()
    const principles = resource.principles.slice(0, 4)
    const verification = strongestVerification(resource.verification_detail)

    return (
        <Link className="card card--link" to={`/resources/${resource.id}`}>
            <div className="item__head">
                <h3 className="item__title">
                    <span className="item__icon">{resource.type_detail?.icon ?? '•'}</span>{' '}
                    {resource.name}
                </h3>
                {resource.usage_count > 0 ? (
                    <Chip variant="accent" title={t('resource.declaredBy')}>
                        {resource.usage_count} {t('resource.usedBy')}
                    </Chip>
                ) : null}
            </div>
            {verification ? (
                <VerificationBadge
                    verification={verification}
                    verifier={
                        verification.verifier_detail?.acronym ??
                        (verification.verifier_detail
                            ? localized(verification.verifier_detail.name)
                            : verification.verifier)
                    }
                    compact
                    extra={resource.verification.length - 1}
                />
            ) : null}
            <p className="item__desc">{localized(resource.description)}</p>
            <div className="chips">
                {resource.type_detail ? <Chip>{localized(resource.type_detail.label)}</Chip> : null}
                <ScopeChip scope={resource.scope} />
                {principles.map((principle) => (
                    <PrincipleChip key={principle} id={principle} />
                ))}
                {resource.principles.length > principles.length ? (
                    <Chip
                        variant="outline"
                        title={resource.principles.slice(principles.length).join(', ')}
                    >
                        +{resource.principles.length - principles.length}
                    </Chip>
                ) : null}
            </div>
        </Link>
    )
}

export function FipCard({
    fip,
    community,
    verifiers,
}: {
    fip: Fip
    community?: Organization | null
    verifiers?: Map<string, Organization>
}) {
    const { t, localized, formatDate } = useI18n()
    const principles = new Set(fip.declarations.map((item) => item.principle))
    const resources = new Set(fip.declarations.map((item) => item.resource))
    const verification = strongestVerification(fip.verification)
    const verifier = verification ? verifiers?.get(verification.verifier) : undefined

    return (
        <Link className="card card--link" to={`/profiles/${fip.id}`}>
            <div className="item__head">
                <h3 className="item__title">{localized(fip.title)}</h3>
                <Chip variant={fip.status === 'published' ? 'in-use' : 'under-consideration'}>
                    {t(`fip.status.${fip.status}`)}
                </Chip>
            </div>
            {verification ? (
                <VerificationBadge
                    verification={verification}
                    verifier={
                        verifier?.acronym ??
                        (verifier ? localized(verifier.name) : verification.verifier)
                    }
                    compact
                    extra={fip.verification.length - 1}
                />
            ) : null}
            <p className="item__desc">{localized(fip.summary)}</p>
            <div className="chips">
                <Chip variant="outline">{localized(fip.domain)}</Chip>
                <Chip>
                    {principles.size}/15 {t('fip.principlesCovered').toLowerCase()}
                </Chip>
                <Chip>
                    {resources.size} {t('resources.count')}
                </Chip>
            </div>
            <p className="item__meta" style={{ marginTop: '0.6rem' }}>
                {community ? `${localized(community.name)} · ` : ''}v{fip.version} ·{' '}
                {formatDate(fip.updated)}
            </p>
        </Link>
    )
}
